import { shallowEqual, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import Strings from 'js/helpers/Strings';
import Commons from 'js/helpers/Commons';
import ListPropertyPreview from 'js/constants/listPropertyPreview';
import getUserSettingsFromStore from 'js/selectors/getUserSettings';
import {
    LISTING_TYPE_V2,
    LOCALIZED_ACTIVE_LISTING_ONLY_SETTING,
    LOCALIZED_RADIUS_SORT_BY_SETTING,
    LOCALIZED_SORT_BY_SETTING,
    RAPID_PROPERTY_TYPE_V2
} from 'js/helpers/Localization';
import OTM from 'js/constants/otm';
import SearchFilterHelper from 'js/helpers/search/SearchFilterHelper';
import { RENTAL_LAST_CAMPAIGN_LAST_LISTED_PERIOD, SALES_LAST_CAMPAIGN_LISTED_TYPE_PARAM, RADIUS_PARAM, SUBURB_ONLY, MAP_SEARCH } from 'js/constants/batchSearch/batchSearch';
import RapidLocalisationHelper from 'js/helpers/search/RapidLocalisationHelper';
import RapidSearchConstant from '../constants/rapidSearch';

const SELECTED_ALL_FILTERS = ['type', 'salesLastCampaignListedType'];
const EVERY_PROPERTY_TYPE = RAPID_PROPERTY_TYPE_V2.map(opt => opt.value);
const EVERY_LISTING_TYPE = LISTING_TYPE_V2.map(listingType => listingType.value);
const getFilterUserSettings = props => state => {
    const forSale = props.isSavedSearch ?
        `${props?.filters?.forSale?.isActiveForSaleCampaign ?? false}` :
        getUserSettingsFromStore(
            state.userPreferences,
            LOCALIZED_ACTIVE_LISTING_ONLY_SETTING[OTM.FOR_SALE],
        );
    const forRent = props.isSavedSearch ?
        `${props?.filters?.forRent?.isActiveForRentCampaign ?? false}` :
        getUserSettingsFromStore(
            state.userPreferences,
            LOCALIZED_ACTIVE_LISTING_ONLY_SETTING[OTM.FOR_RENT],
        );
    const SORT_BY_SETTING = props.isRadiusSearch
        ? LOCALIZED_RADIUS_SORT_BY_SETTING
        : LOCALIZED_SORT_BY_SETTING;
    return ({
        activeListingsOnly: {
            forSale,
            forRent,
        },
        sort: {
            all: getUserSettingsFromStore(
                state.userPreferences,
                SORT_BY_SETTING[OTM.ALL],
            ) ?? props.sort,
            recentSale: getUserSettingsFromStore(
                state.userPreferences,
                SORT_BY_SETTING[OTM.RECENT_SALE],
            ) ?? props.sort,
            forSale: getUserSettingsFromStore(
                state.userPreferences,
                SORT_BY_SETTING[OTM.FOR_SALE],
            ) ?? props.sort,
            forRent: getUserSettingsFromStore(
                state.userPreferences,
                SORT_BY_SETTING[OTM.FOR_RENT],
            ) ?? props.sort,
            aged: getUserSettingsFromStore(
                state.userPreferences,
                SORT_BY_SETTING[OTM.AGED],
            ) ?? props.sort,
            withdrawn: getUserSettingsFromStore(
                state.userPreferences,
                SORT_BY_SETTING[OTM.WITHDRAWN],
            ) ?? props.sort,
            tenure: getUserSettingsFromStore(
                state.userPreferences,
                SORT_BY_SETTING[OTM.TENURE],
            ) ?? props.sort,
        },
    })
};
export default (props) => {
    const userSettings = useSelector(getFilterUserSettings(props));
    const { statusType } = props;
    const radiusSearchTargetProperty = useSelector(store => store.rapid.get('targetProperty').value, shallowEqual);
    const defaultFilters = useMemo(() => {
        const buildStatusTypeFilters = (sType, additionalFilters) => {
            let sortSource = RapidSearchConstant.SEARCH_DEFAULT_SORT.ADDRESS;
            if (!props.savedList) {
                sortSource = props.filters?.[sType]?.sort ?? userSettings.sort[sType];
            }
            const sort = Strings.isValid(sortSource) ?
                JSON.parse(sortSource) : sortSource;
            return {
                ...(props.filters?.[sType] ?? {}),
                ...(additionalFilters ?? {}),
                sort,
            };
        };
        const getActiveListingOnlyValue = (type) => props?.filtersFromUrl?.hasOwnProperty('isActiveListingsOnly')
            ? props.filtersFromUrl.isActiveListingsOnly
            : Commons.parseBoolean(userSettings.activeListingsOnly[type])
        return SearchFilterHelper.getDefaultFiltersV2({
            [OTM.ALL]: buildStatusTypeFilters(OTM.ALL),
            [OTM.RECENT_SALE]: buildStatusTypeFilters(OTM.RECENT_SALE),
            [OTM.FOR_SALE]: buildStatusTypeFilters(OTM.FOR_SALE, {
                isActiveListingsOnly: getActiveListingOnlyValue('forSale'),
            }),
            [OTM.FOR_RENT]: buildStatusTypeFilters(OTM.FOR_RENT, {
                isActiveListingsOnly: getActiveListingOnlyValue('forRent'),
            }),
            [OTM.AGED]: buildStatusTypeFilters(OTM.AGED),
            [OTM.WITHDRAWN]: buildStatusTypeFilters(OTM.WITHDRAWN),
            [OTM.TENURE]: buildStatusTypeFilters(OTM.TENURE),
            base: {
                ...(props.baseFilters ?? {}),
                isNonAddressSearch: props.searchType !== ListPropertyPreview.SEARCH_TYPE.ADDRESS,
            },
        });
    }, [JSON.stringify(props.baseFilters), JSON.stringify(props.filters)]);
    const [filters, setFilters] = useState(defaultFilters);
    const [previousFilters, setPreviousFilters] = useState(defaultFilters);
    const [disabled, setDisabled] = useState(false);

    // Sync hold period defaults when averageHoldTrend updates from the API.
    // Only sets holdPeriodHouse/holdPeriodUnit when the current filter state does not
    // already have user-supplied values (e.g. URL-decoded permalink values). This prevents
    // the async API response from overwriting URL values on VIC addresses, which render
    // before the averageHoldTrend API returns.
    // Also syncs type, but only when the current type is empty (all-selected state) —
    // this keeps House/Unit checkboxes checked after API load without overwriting an
    // explicit user selection like ['Unit'].
    useEffect(() => {
        const tenureFilters = defaultFilters[OTM.TENURE];
        const { type, holdPeriodHouse, holdPeriodUnit } = tenureFilters || {};
        if (holdPeriodHouse || holdPeriodUnit) {
            const buildUpdate = (prevTenure) => ({
                ...(!prevTenure.holdPeriodHouse ? { holdPeriodHouse } : {}),
                ...(!prevTenure.holdPeriodUnit ? { holdPeriodUnit } : {}),
                ...(prevTenure.type?.length === 0 && type?.length ? { type } : {}),
            });
            setFilters(prevFilters => ({
                ...prevFilters,
                [OTM.TENURE]: {
                    ...prevFilters[OTM.TENURE],
                    ...buildUpdate(prevFilters[OTM.TENURE]),
                },
            }));
            setPreviousFilters(prevFilters => ({
                ...prevFilters,
                [OTM.TENURE]: {
                    ...prevFilters[OTM.TENURE],
                    ...buildUpdate(prevFilters[OTM.TENURE]),
                },
            }));
        }
    }, [defaultFilters[OTM.TENURE]?.holdPeriodHouse, defaultFilters[OTM.TENURE]?.holdPeriodUnit]);

    const getMultiCheckbox = (source, fieldName, everyType) =>
        source[statusType][fieldName].length ? source[statusType][fieldName] : everyType;
    const getFilters = (_filters = {}, _statusType = statusType) => {
        const processedFilters = SearchFilterHelper.getRapidFilters(
            {
                ...filters[_statusType],
                ..._filters,
                ...RapidLocalisationHelper.getRapidLocalityParamObj(radiusSearchTargetProperty)
            },
            _statusType,
        );
        return {
            ...(props.baseFilters ?? {}),
            ...processedFilters,
        };
    };

    const mergeRadiusToRawFilters = (_statusType, prevRawFilters, filter) => {
        const { radius, suburbOnly } = filters[statusType];
        return {
            ...prevRawFilters[_statusType],
            radius,
            suburbOnly,
            ...filter,
        };
    };

    const constructNewFilters = (filter, isGlobal, _filters = filters, _statusType = statusType) => {
        if (isGlobal) {
            return {
                [OTM.ALL]: mergeRadiusToRawFilters(OTM.ALL, _filters, filter),
                [OTM.RECENT_SALE]: mergeRadiusToRawFilters(OTM.RECENT_SALE, _filters, filter),
                [OTM.FOR_SALE]: mergeRadiusToRawFilters(OTM.FOR_SALE, _filters, filter),
                [OTM.FOR_RENT]: mergeRadiusToRawFilters(OTM.FOR_RENT, _filters, filter),
                [OTM.AGED]: mergeRadiusToRawFilters(OTM.AGED, _filters, filter),
                [OTM.WITHDRAWN]: mergeRadiusToRawFilters(OTM.WITHDRAWN, _filters, filter),
                [OTM.TENURE]: mergeRadiusToRawFilters(OTM.TENURE, _filters, filter),
            };
        } else {
            return {
                ..._filters,
                [_statusType]: {
                    ..._filters[_statusType],
                    ...filter,
                },
            };
        }
    };

    const update = (filter, isGlobal) =>
        setFilters(_rawFilters => constructNewFilters(filter, isGlobal, _rawFilters));

    const reset = (isInit) => {
        setFilters(includeMapSearch(filters, defaultFilters, isInit));
        setPreviousFilters(includeMapSearch(filters, defaultFilters, isInit));
    };

    const includeMapSearch = (currentFiltersWithOrWithoutMapSearch, defaultFilters, isInit) => {
        return Object.entries(defaultFilters).reduce((acc, [key, value]) => ({
            ...acc,
            [key]: {
                ...value,
                ...(!isInit && currentFiltersWithOrWithoutMapSearch[key]?.mapSearch && {
                    mapSearch: currentFiltersWithOrWithoutMapSearch[key].mapSearch
                }),
            }
        }), {});
    }

    const isModified = (fieldName) => {
        const filter = filters[statusType][fieldName];
        if (typeof filter === 'object') {
            return !Commons.isEqual(filter, previousFilters[statusType][fieldName]);
        }
        return filter !== previousFilters[statusType][fieldName];
    };

    const save = (immediateFilters, _statusType) => {
        const mapSearchChanged = MAP_SEARCH in immediateFilters
            && !Commons.isEqual(immediateFilters[MAP_SEARCH], previousFilters[statusType][MAP_SEARCH])
        const radiusFiltersChanged = (props.isRadiusSearch
            && (isModified(RADIUS_PARAM) || isModified(SUBURB_ONLY)))
        || mapSearchChanged;
        const _filters = constructNewFilters(
            immediateFilters,
            radiusFiltersChanged,
            filters, _statusType,
        );
        setPreviousFilters(_filters);
        setFilters(_filters);
    };

    const isDefaultValue = (name, customStatusType) => {
        const _statusType = customStatusType ?? statusType;
        const rawValue = filters[_statusType][name];
        if (SELECTED_ALL_FILTERS.includes(name) && !rawValue.length) {
            return true;
        }
        // Match `km` and `Km`.
        // Safest fix for now, since we are not sure if somewhere in code is expecting `km`.
        // Will be cleaned up once old design is removed.
        if (name === 'radius') {
            const radiusRaw = filters[_statusType][name];
            const radiusDefault = defaultFilters[_statusType][name];
            return Strings.isEqualIgnoreCase(`${radiusRaw}`, `${radiusDefault}`);
        }

        return Commons.isEqual(
            defaultFilters[_statusType][name],
            rawValue,
        );
    };

    const isZeroValue = (name, _filters = filters) => {
        if (_filters[statusType] && _filters[statusType][name]) {
            const rawMin = parseInt(_filters[statusType][name].min?.replace('$', ''));
            const rawMax = parseInt(_filters[statusType][name].max?.replace('$', ''));
            return (rawMin === 0 && Number.isNaN(rawMax))
                || ((Number.isNaN(rawMin) || rawMin === 0) && rawMax === 0);
        }
        return false;
    };

    const dirty = useMemo(() => {
        let rentalLastCampaignLastListedPeriod = undefined;
        if (RENTAL_LAST_CAMPAIGN_LAST_LISTED_PERIOD in filters[statusType]
            && (filters[statusType].rentalLastCampaignLastListedPrice?.min
                || filters[statusType].rentalLastCampaignLastListedPrice?.max)) {
            rentalLastCampaignLastListedPeriod = filters[statusType].rentalLastCampaignLastListedPeriod;
        }
        return !Commons.isEqual({
            ...filters[statusType],
            rentalLastCampaignLastListedPeriod,
            type: getMultiCheckbox(filters, 'type', EVERY_PROPERTY_TYPE),
            salesLastCampaignListedType: getMultiCheckbox(filters, SALES_LAST_CAMPAIGN_LISTED_TYPE_PARAM, EVERY_LISTING_TYPE),
        }, {
            ...previousFilters[statusType],
            rentalLastCampaignLastListedPeriod,
            type: getMultiCheckbox(previousFilters, 'type', EVERY_PROPERTY_TYPE),
            salesLastCampaignListedType: getMultiCheckbox(previousFilters, SALES_LAST_CAMPAIGN_LISTED_TYPE_PARAM, EVERY_LISTING_TYPE),
        });
    }, [filters, previousFilters, statusType]);

    const isResetDisabled = useMemo(() => {
        let rentalLastCampaignLastListedPeriod = undefined;
        if (RENTAL_LAST_CAMPAIGN_LAST_LISTED_PERIOD in filters[statusType]
            && (filters[statusType].rentalLastCampaignLastListedPrice?.min
                || filters[statusType].rentalLastCampaignLastListedPrice?.max)) {
            rentalLastCampaignLastListedPeriod = filters[statusType].rentalLastCampaignLastListedPeriod;
        }
        return Commons.isEqual(getFilters(defaultFilters[statusType]),
            //exclude mapSearch from comparison
            (({ mapSearch, ...restOfTheFilters }) => restOfTheFilters)(getFilters({
            rentalLastCampaignLastListedPeriod,
            type: getMultiCheckbox(filters, 'type', EVERY_PROPERTY_TYPE),
            salesLastCampaignListedType: getMultiCheckbox(filters, SALES_LAST_CAMPAIGN_LISTED_TYPE_PARAM, EVERY_LISTING_TYPE),
        })));
    }, [defaultFilters, filters, statusType]);

    return {
        defaultValue: defaultFilters,
        value: filters,
        previousValue: previousFilters,
        disabled,
        setDisabled,
        dirty,
        getFlatFilters: getFilters,
        isModified,
        save,
        reset,
        update,
        isDefaultValue,
        isZeroValue,
        isResetDisabled,
        includeMapSearch,
    }
};
